type InfiniteScrollDeps = {
  root: HTMLElement;
  originSelector: string;
  nextSelector: string;
  loadingImageSrc: string;
};

class InfiniteScroll {
  private readonly root: HTMLElement;
  private readonly originSelector: string;
  private readonly nextSelector: string;
  private readonly loadingImageSrc: string;
  private readonly scrollObserver: IntersectionObserver;
  private readonly mutationObserver: MutationObserver;

  static init(
    rootSelector: string,
    originSelector: string,
    nextSelector: string
  ): InfiniteScroll | null {
    const root = document.querySelector<HTMLElement>(rootSelector);
    if (!root) {
      console.error(`InfiniteScroll: ${rootSelector} not found`);
      return null;
    }
    const loadingImageSrc = root.dataset.loadingImage;
    if (!loadingImageSrc) {
      console.error(
        `InfiniteScroll: ${rootSelector} requires data-loading-image`
      );
      return null;
    }
    return new InfiniteScroll({
      root,
      originSelector,
      nextSelector,
      loadingImageSrc,
    });
  }

  private constructor({
    root,
    originSelector,
    nextSelector,
    loadingImageSrc,
  }: InfiniteScrollDeps) {
    this.root = root;
    this.originSelector = originSelector;
    this.nextSelector = nextSelector;
    this.loadingImageSrc = loadingImageSrc;
    this.scrollObserver = this.buildScrollObserver();
    this.mutationObserver = this.buildMutationObserver();
    this.observeLastOrigin();
    this.mutationObserver.observe(this.root, {
      childList: true,
      subtree: true,
    });
  }

  private buildScrollObserver(): IntersectionObserver {
    return new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.scrollObserver.disconnect();
        const nextLink = this.root.querySelector<HTMLElement>(
          this.nextSelector
        );
        if (!nextLink) return;
        this.root.insertAdjacentElement(
          'beforeend',
          this.createLoadingImage()
        );
        nextLink.click();
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );
  }

  private buildMutationObserver(): MutationObserver {
    return new MutationObserver((mutations) => {
      const originAdded = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).matches(this.originSelector)
        )
      );
      if (!originAdded) return;

      this.root.querySelector('.js-tmp-loading')?.remove();
      if (this.root.querySelector(this.nextSelector)) {
        this.observeLastOrigin();
      }
    });
  }

  private observeLastOrigin(): void {
    const last = this.lastOrigin();
    if (last) this.scrollObserver.observe(last);
  }

  private lastOrigin(): Element | null {
    const origins = this.root.querySelectorAll(this.originSelector);
    return origins.item(origins.length - 1);
  }

  private createLoadingImage(): HTMLDivElement {
    const div = document.createElement('div');
    div.classList.add('js-tmp-loading', 'text-center', 'p-3.5');
    const img = document.createElement('img');
    img.src = this.loadingImageSrc;
    div.appendChild(img);
    return div;
  }
}

export default InfiniteScroll;
